import React, { useEffect, useState } from 'react';
import { GetRequest } from 'components/common/index.js';

const PricingCards = ({ setMode, setSubscription, setCurrencyName }) => {
  const [subscriptionOneCredit, setSubscriptionOneCredit] = useState(undefined);
  const [subscriptionFiveCredits, setSubscriptionFiveCredits] = useState(undefined);
  const [subscriptionTenCredits, setSubscriptionTenCredits] = useState(undefined);
  const [monthlySubscription, setMonthlySubscription] = useState(undefined);
  const [annualSubscription, setAnnualSubscription] = useState(undefined);
  const [selectedCreditType, setSelectedCreditType] = useState('ten_credits');

  const fetchSubscriptions = () => {
    GetRequest({
      url: '/charge/subscriptions',
      callback: (data) => {
        setCurrencyName(data.currency_name);
        setSubscriptionOneCredit((_prev) => { return { ...data.one_credit }; });
        setSubscriptionFiveCredits((_prev) => { return { ...data.five_credits }; });
        setSubscriptionTenCredits((_prev) => { return { ...data.ten_credits }; });
        setMonthlySubscription((_prev) => { return { ...data.monthly_subscription }; });
        setAnnualSubscription((_prev) => { return { ...data.annual_subscription }; });
      },
    });
  };

  useEffect(fetchSubscriptions, []);
  useEffect(setUpPreviewExplainedTooltip, []);
  useEffect(setUpManufacturingCostTooltip, []);

  useEffect(() => {
    const listen = 'show_drawing.currency_changed';
    document.addEventListener(listen, fetchSubscriptions);
    return () => { document.removeEventListener(listen, fetchSubscriptions); };
  }, []);

  useEffect(() => {
    $('.offer_ends').each(offerExpiresAt);
  });

  const selectedCreditSubscription = () => {
    if (selectedCreditType === 'one_credit')   { return subscriptionOneCredit; }
    if (selectedCreditType === 'five_credits') { return subscriptionFiveCredits; }
    if (selectedCreditType === 'ten_credits')  { return subscriptionTenCredits; }
    return undefined;
  };

  const handleCreditCardCTA = (e) => {
    e.preventDefault();
    const subscription = selectedCreditSubscription();
    if (subscription === undefined) { return; }
    setSubscription(() => subscription);
    setMode('payment_form');
  };

  const handleSubscriptionCardCTA = (subscription) => (e) => {
    e.preventDefault();
    setSubscription(() => subscription);
    setMode('payment_form');
  };

  const creditCTAWording = {
    one_credit:   t('sign_up.purchase_one_credit'),
    five_credits: t('sign_up.purchase_five_credits'),
    ten_credits:  t('sign_up.purchase_ten_credits'),
  };

  const creditOptions = [
    { type: 'one_credit',   subscription: subscriptionOneCredit   },
    { type: 'five_credits', subscription: subscriptionFiveCredits },
    { type: 'ten_credits',  subscription: subscriptionTenCredits  },
  ];

  const renderSubscriptionPrice = (subscription) => {
    if (subscription === undefined) { return null; }
    if (subscription.has_discount) {
      return (
        <>
          <strike className="pricing-card-price-old">{subscription.price_without_discount_formatted}</strike>
          <br />
          <span className="pricing-card-price-new">{subscription.price_formatted}</span>
        </>
      );
    }
    return <span className="pricing-card-price-new">{subscription.price_formatted}</span>;
  };

  const renderOfferBadge = (subscription) => {
    if (subscription === undefined || !subscription.has_discount) { return null; }
    return (
      <div className="pricing-card-offer">
        <span className="badge badge-dark">
          {subscription.coupon_code_expires_at != null ? (
            <span data-expires-at={subscription.coupon_code_expires_at} className="offer_ends" />
          ) : (
            t('sign_up.special_offer')
          )}
        </span>
      </div>
    );
  };

  const checkIcon = <i className="fa fa-check pricing-card-check" aria-hidden="true" />;

  const renderFeatureWithHtml = (translationKey) => {
    return <span dangerouslySetInnerHTML={{ __html: t(translationKey) }} />;
  };

  const renderCreditOptionRow = ({ type, subscription }) => {
    if (subscription === undefined) { return null; }
    const isSelected = selectedCreditType === type;
    return (
      <label
        key={type}
        className={`pricing-card-credit-option ${isSelected ? 'selected' : ''}`}
        onClick={() => setSelectedCreditType(type)}
      >
        <div className="pricing-card-credit-option-radio">
          <input
            type="radio"
            name="pricing_cards_credit_type"
            value={type}
            checked={isSelected}
            onChange={() => setSelectedCreditType(type)}
          />
        </div>
        <div className="pricing-card-credit-option-label">
          <div className="pricing-card-credit-option-title">
            <strong>{t(`sign_up.${type}`)}</strong>
          </div>
          <div className="pricing-card-credit-option-detail">
            <small>
              {t(`sign_up.preview_wording_${type}`)} <sup>*</sup>, {t(`sign_up.limited_wording_${type}`)}
            </small>
          </div>
        </div>
        <div className="pricing-card-credit-option-price">
          {subscription.has_discount ? (
            <>
              <strike className="pricing-card-price-old"><small>{subscription.price_without_discount_formatted}</small></strike>
              <br />
              <strong>{subscription.price_formatted}</strong>
            </>
          ) : (
            <strong>{subscription.price_formatted}</strong>
          )}
          {subscription.unit_price_formatted !== subscription.price_formatted && (
            <div className="pricing-card-credit-option-unit-price">
              <small dangerouslySetInnerHTML={{
                __html: t('sign_up.price_per_dieline')
                  .replace('%{price}', subscription.unit_price_formatted)
                  .replace('<br />', ''),
              }} />
            </div>
          )}
        </div>
      </label>
    );
  };

  const renderSubscriptionCard = ({ subscription, title, periodLabel, ctaText, featured }) => {
    return (
      <div className={`pricing-card ${featured ? 'pricing-card-featured' : ''}`}>
        {featured && <div className="pricing-card-badge">Most Popular</div>}
        <div className="pricing-card-header">
          <h4>{title}</h4>
        </div>

        <div className="pricing-card-features">
          <ul>
            <li>{checkIcon} {renderFeatureWithHtml('sign_up.professional_dielines')}</li>
            <li>{checkIcon} {t('sign_up.unlimited_access')}</li>
            <li>{checkIcon} {renderFeatureWithHtml(`sign_up.preview_wording_${subscription !== undefined ? subscription.type : 'monthly'}`)}</li>
            <li>{checkIcon} {t('sign_up.manufacturing_cost_calculator')} <sup>**</sup></li>
            <li>{checkIcon} {renderFeatureWithHtml('sign_up.no_contract')}</li>
            <li>{checkIcon} {t('sign_up.auto_renew')}</li>
          </ul>
        </div>

        {renderOfferBadge(subscription)}

        <div className="pricing-card-price">
          {renderSubscriptionPrice(subscription)}
          <div className="pricing-card-price-period">
            <small>{periodLabel}</small>
          </div>
        </div>

        <div className="pricing-card-cta">
          {subscription !== undefined ? (
            <a className="btn simple-btn pricing-card-btn" href="#" onClick={handleSubscriptionCardCTA(subscription)}>
              {ctaText}
            </a>
          ) : (
            <a className="btn simple-btn pricing-card-btn pricing-card-btn-disabled" href="#" onClick={(e) => e.preventDefault()}>
              {t('sign_up.please_wait')}
            </a>
          )}
        </div>
      </div>
    );
  };

  const selectedCredit = selectedCreditSubscription();

  return (
    <div className="pricing-cards-container">
      <div className="pricing-cards-grid">

        <div className="pricing-card">
          <div className="pricing-card-header">
            <h4>{t('sign_up.credits')}</h4>
          </div>

          <div className="pricing-card-features">
            <ul>
              <li>{checkIcon} {renderFeatureWithHtml('sign_up.professional_dielines')}</li>
              <li>{checkIcon} {t('sign_up.time_limited_previews')} <sup>*</sup></li>
            </ul>
          </div>

          <div className="pricing-card-credit-options">
            {creditOptions.map(renderCreditOptionRow)}
          </div>

          {renderOfferBadge(selectedCredit)}

          <div className="pricing-card-cta">
            {selectedCredit !== undefined ? (
              <a className="btn simple-btn pricing-card-btn" href="#" onClick={handleCreditCardCTA}>
                {creditCTAWording[selectedCreditType]}
              </a>
            ) : (
              <a className="btn simple-btn pricing-card-btn pricing-card-btn-disabled" href="#" onClick={(e) => e.preventDefault()}>
                {t('sign_up.please_wait')}
              </a>
            )}
          </div>
        </div>

        {renderSubscriptionCard({
          subscription: monthlySubscription,
          title: t('sign_up.monthly'),
          periodLabel: t('sign_up.per_month'),
          ctaText: t('sign_up.purchase_monthly_subscription'),
          featured: false,
        })}

        {renderSubscriptionCard({
          subscription: annualSubscription,
          title: t('sign_up.annually'),
          periodLabel: t('sign_up.per_year'),
          ctaText: t('sign_up.purchase_annual_subscription'),
          featured: true,
        })}

      </div>

      {window.TrustPilotEnabled && window.TrustpilotRating && (
        <a
          className="pricing-cards-trust-signal"
          href="/out?key=all_reviews"
          target="_blank"
          rel="noopener"
        >
          <div className="pricing-cards-trust-signal-stars">
            <div
              className={`star_rating_${window.TrustpilotRating.replace('.', '_')}`}
              role="img"
              aria-label={`${window.TrustpilotRating} out of 5 stars`}
            ></div>
          </div>
          <span className="pricing-cards-trust-signal-text">
            Rated {window.TrustpilotRating} out of 5 &mdash; 100+ reviews
          </span>
        </a>
      )}

      <div className="pricing-cards-footnotes">
        <small>
          <sup>*</sup> {t('sign_up.what_is_preview')}
          <span className="abc_helper_tooltip">
            <a href="#" className="preview_explained" title={t('sign_up.more_about_previews')}>
              <i className="zmdi zmdi-pin-help"></i>
            </a>
          </span>
        </small>
        <br />
        <small>
          <sup>**</sup> {t('sign_up.what_is_manufacturing_cost_calculator')}
          <span className="abc_helper_tooltip">
            <a href="#" className="manufacturing_cost_calulator_explained" title={t('sign_up.more_about_manufacturing_cost_calulator')}>
              <i className="zmdi zmdi-pin-help"></i>
            </a>
          </span>
        </small>
      </div>
    </div>
  );
};

export default PricingCards;
